import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * One mounted filesystem's usage, as reported by the box.
 *
 * @since 1.0.0
 */
export const DiskUsage = Schema.Struct({
  path: Schema.String,
  mount: Schema.String,
  totalBytes: Schema.Number,
  usedBytes: Schema.Number,
  availableBytes: Schema.Number,
});
export type DiskUsage = Schema.Schema.Type<typeof DiskUsage>;

/**
 * The box's latest system vitals. Each gauge is `null` when the box could not
 * read it; `observedAt` is the box's own ISO capture time. At most 4 disks.
 *
 * Stored as `agent_installation_stats.system_vitals` and reused by the RPC
 * layer as the wire schema.
 *
 * @since 1.0.0
 */
export const SystemVitals = Schema.Struct({
  cpuPercent: Schema.NullOr(Schema.Number),
  memoryPercent: Schema.NullOr(Schema.Number),
  memoryTotalBytes: Schema.NullOr(Schema.Number),
  disks: Schema.Array(DiskUsage).pipe(Schema.maxItems(4)),
  observedAt: Schema.String,
});
export type SystemVitals = Schema.Schema.Type<typeof SystemVitals>;

/**
 * Why one OpenClaw stats probe produced no data.
 *
 * @since 1.0.0
 */
export const OcProbeError = Schema.Literal(
  "ProbeMissing",
  "ProbeTimeout",
  "ProbeFailed",
  "ProbeMalformed",
  "ProbeUnsupported",
);
export type OcProbeError = Schema.Schema.Type<typeof OcProbeError>;

/**
 * One OpenClaw agent's memory-index stats.
 *
 * @since 1.0.0
 */
export const OcMemoryAgentStats = Schema.Struct({
  agentId: Schema.String,
  backend: Schema.String,
  provider: Schema.NullOr(Schema.String),
  model: Schema.NullOr(Schema.String),
  indexedFiles: Schema.NullOr(Schema.Number),
  totalFiles: Schema.NullOr(Schema.Number),
  chunks: Schema.NullOr(Schema.Number),
  dirty: Schema.NullOr(Schema.Boolean),
  databaseBytes: Schema.NullOr(Schema.Number),
  ftsAvailable: Schema.NullOr(Schema.Boolean),
  vectorEnabled: Schema.NullOr(Schema.Boolean),
  lastSyncError: Schema.NullOr(Schema.String),
});
export type OcMemoryAgentStats = Schema.Schema.Type<typeof OcMemoryAgentStats>;

/**
 * The OpenClaw workspace's size, file counts and last git commit.
 *
 * @since 1.0.0
 */
export const OcWorkspaceStats = Schema.Struct({
  path: Schema.String,
  sizeBytes: Schema.NullOr(Schema.Number),
  fileCount: Schema.NullOr(Schema.Number),
  dirtyCount: Schema.NullOr(Schema.Number),
  lastCommit: Schema.NullOr(Schema.Struct({
    sha: Schema.String,
    committedAt: Schema.String,
    subject: Schema.String,
  })),
});
export type OcWorkspaceStats = Schema.Schema.Type<typeof OcWorkspaceStats>;

/**
 * One installed OpenClaw skill.
 *
 * @since 1.0.0
 */
export const OcInstalledSkill = Schema.Struct({
  name: Schema.String,
  source: Schema.String,
  eligible: Schema.Boolean,
  disabled: Schema.Boolean,
});
export type OcInstalledSkill = Schema.Schema.Type<typeof OcInstalledSkill>;

/**
 * The box's latest OpenClaw stats report. Each section (`memory`, `workspace`,
 * `skills`) is `null` when its probe failed, with the paired `*Error` naming
 * why. `observedAt` is the box's own ISO capture time. Bounded: at most 20
 * memory agents and 200 installed skills.
 *
 * Stored as `agent_installation_stats.oc_stats` and reused by the RPC layer as
 * the wire schema.
 *
 * @since 1.0.0
 */
export const OcStatsReport = Schema.Struct({
  observedAt: Schema.String,
  memory: Schema.NullOr(Schema.Array(OcMemoryAgentStats).pipe(Schema.maxItems(20))),
  memoryError: Schema.NullOr(OcProbeError),
  workspace: Schema.NullOr(OcWorkspaceStats),
  workspaceError: Schema.NullOr(OcProbeError),
  skills: Schema.NullOr(Schema.Struct({
    installed: Schema.Array(OcInstalledSkill).pipe(Schema.maxItems(200)),
    bundledCount: Schema.Number,
    eligibleCount: Schema.Number,
  })),
  skillsError: Schema.NullOr(OcProbeError),
});
export type OcStatsReport = Schema.Schema.Type<typeof OcStatsReport>;

/**
 * AgentInstallationStats model — the latest box vitals and OpenClaw stats for
 * one installation (one row per install, keyed by `installationId`).
 *
 * Each report is `null` until the box first sends it; its `*ObservedAt` is the
 * server's receive time of the latest report.
 *
 * @since 1.0.0
 */
export class AgentInstallationStats extends Model.Class<AgentInstallationStats>("AgentInstallationStats")({
  installationId: Schema.String,
  systemVitals: Schema.NullOr(SystemVitals),
  systemVitalsObservedAt: Schema.NullOr(Schema.DateTimeUtc),
  ocStats: Schema.NullOr(OcStatsReport),
  ocStatsObservedAt: Schema.NullOr(Schema.DateTimeUtc),
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}
